import { useCallback, useEffect, useRef, useState } from 'react';
import CircularProgress from '@mui/material/CircularProgress';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import DoneAllRoundedIcon from '@mui/icons-material/DoneAllRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import NotificationsNoneRoundedIcon from '@mui/icons-material/NotificationsNoneRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import Dialog from './TivigoDialog';
import { notificationPlainText, sanitizeNotificationHtml } from './notificationHtml';
import { userApi } from './services/tivigoApi';
import './userPanelNotificationDetail.css';

const NOTIFICATION_LIST_LIMIT = 20;
const NOTIFICATION_CLEAR_BATCH_LIMIT = 50;

const notificationDate = (value) => {
  if (!value) return null;
  const numeric = Number(value);
  const date = Number.isFinite(numeric) && numeric > 0
    ? new Date(numeric > 100000000000 ? numeric : numeric * 1000)
    : new Date(String(value).replace(' ', 'T'));
  return Number.isNaN(date.getTime()) ? null : date;
};

const relativeTime = (value) => {
  const date = notificationDate(value);
  if (!date) return 'Tarih bilgisi yok';
  const seconds = Math.max(0, Math.floor((Date.now() - date.getTime()) / 1000));
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  if (minutes < 1) return 'Az önce';
  if (minutes < 60) return minutes === 1 ? '1 dakika önce' : `${minutes} dakika önce`;
  if (hours < 24) return hours === 1 ? '1 saat önce' : `${hours} saat önce`;
  if (days === 1) return 'Dün';
  return `${days} gün önce`;
};

const notificationContent = (notification) => String(
  notification?.icerik
  ?? notification?.mesaj
  ?? notification?.content
  ?? notification?.message
  ?? ''
);

const notificationTitle = (notification) => String(notification?.baslik ?? notification?.title ?? 'Bildirim');

const regionalNotificationContent = (notification, currency) => {
  const content = notificationContent(notification);
  if (currency !== 'EUR') return content;
  const title = notificationTitle(notification);
  if (/hoş geldiniz/i.test(title) && /kupon/i.test(content) && /\bTL\b/i.test(content)) {
    return 'Deneme hesabınız hazır! Tüm içeriklerimizi keşfedebilir ve size uygun Euro paketini seçebilirsiniz.';
  }
  if (/referans bonus/i.test(title) || /\bTL\s*bonusunuz/i.test(content)) {
    return content.replace(/\d+(?:[.,]\d+)?\s*TL\s*bonusunuz/gi, 'Referans bonusunuz');
  }
  return content;
};

const notificationDateLabel = (value) => {
  const date = notificationDate(value);
  if (!date) return 'Tarih bilgisi yok';
  return new Intl.DateTimeFormat('tr-TR', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

function UserPanelNotifications({ onNavigate, currency = 'TRY' }) {
  const rootRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [stats, setStats] = useState({ unread_count: 0, total_count: 0, read_count: 0 });
  const [loading, setLoading] = useState(false);
  const [actionId, setActionId] = useState('');
  const [error, setError] = useState('');
  const [selectedNotification, setSelectedNotification] = useState(null);

  const loadStats = useCallback(async () => {
    try {
      const payload = await userApi.getNotificationStats();
      setStats((current) => ({ ...current, ...(payload?.data || {}) }));
    } catch (requestError) {
      // Sayaç isteği sessiz kalır; panel açıldığında hata görünür.
    }
  }, []);

  const loadNotifications = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const payload = await userApi.getNotifications({ page: 1, limit: NOTIFICATION_LIST_LIMIT });
      const rows = Array.isArray(payload?.data?.notifications) ? payload.data.notifications : [];
      const paginationTotal = Number(payload?.data?.pagination?.total);
      setNotifications(rows);
      setStats((current) => ({
        ...current,
        total_count: Number.isFinite(paginationTotal) && paginationTotal >= 0
          ? paginationTotal
          : rows.length,
      }));
    } catch (requestError) {
      setNotifications([]);
      setError(requestError?.message || 'Bildirimler yüklenemedi.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStats();
    const intervalId = window.setInterval(loadStats, 30000);
    return () => window.clearInterval(intervalId);
  }, [loadStats]);

  useEffect(() => {
    if (!open) return undefined;
    const handlePointerDown = (event) => {
      if (!rootRef.current?.contains(event.target)) setOpen(false);
    };
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);

  const markRead = async (notification) => {
    if (Number(notification?.okundu) === 1) return;
    await userApi.markNotificationRead(notification.id);
    setNotifications((current) => current.map((item) => (item.id === notification.id ? { ...item, okundu: '1' } : item)));
    setSelectedNotification((current) => (current?.id === notification.id ? { ...current, okundu: '1' } : current));
    setStats((current) => ({
      ...current,
      unread_count: Math.max(0, Number(current.unread_count) - 1),
      read_count: Number(current.read_count) + 1,
    }));
  };

  const handleNotificationClick = async (notification) => {
    setSelectedNotification(notification);
    setOpen(false);
    setActionId(`read-${notification.id}`);
    setError('');
    try {
      await markRead(notification);
    } catch (requestError) {
      setError(requestError?.message || 'Bildirim güncellenemedi.');
    } finally {
      setActionId('');
    }
  };

  const deleteNotification = async (notification) => {
    setActionId(`delete-${notification.id}`);
    setError('');
    try {
      if (Number(notification.okundu) === 0) await userApi.markNotificationRead(notification.id);
      await userApi.deleteNotification(notification.id);
      setNotifications((current) => current.filter((item) => item.id !== notification.id));
      setStats((current) => ({
        unread_count: Number(notification.okundu) === 0 ? Math.max(0, Number(current.unread_count) - 1) : Number(current.unread_count),
        read_count: Number(notification.okundu) === 1 ? Math.max(0, Number(current.read_count) - 1) : Number(current.read_count),
        total_count: Math.max(0, Number(current.total_count) - 1),
      }));
      return true;
    } catch (requestError) {
      setError(requestError?.message || 'Bildirim silinemedi.');
      return false;
    } finally {
      setActionId('');
    }
  };

  const clearAll = async () => {
    setActionId('clear-all');
    setError('');
    try {
      // Endpoint toplu silme sunmadığı için her seferinde ilk sayfayı tüket.
      // Böylece ekranda yüklü ilk 20 kayıtla sınırlı kalmadan tamamı silinir.
      while (true) {
        const payload = await userApi.getNotifications({ page: 1, limit: NOTIFICATION_CLEAR_BATCH_LIMIT });
        const rows = Array.isArray(payload?.data?.notifications) ? payload.data.notifications : [];
        if (rows.length === 0) break;

        for (const notification of rows) {
          if (notification?.id === undefined || notification?.id === null || notification?.id === '') {
            throw new Error('Bildirim kimliği alınamadı.');
          }
          // İstekleri sırayla göndererek eşzamanlı silme kaynaklı kısmi sonuçları önle.
          await userApi.deleteNotification(notification.id);
        }
      }

      setNotifications([]);
      setStats({ unread_count: 0, total_count: 0, read_count: 0 });
      await loadStats();
    } catch (requestError) {
      await Promise.all([loadNotifications(), loadStats()]);
      setError(requestError?.message || 'Bildirimler temizlenemedi.');
    } finally {
      setActionId('');
    }
  };

  const unreadCount = Math.max(0, Number(stats.unread_count) || 0);
  const selectedContent = regionalNotificationContent(selectedNotification, currency);
  const canNavigateFromDetail = Boolean(
    selectedNotification
    && typeof onNavigate === 'function'
    && (selectedNotification.link || selectedNotification.link_data)
  );

  return (
    <div className="tvup-notifications" ref={rootRef}>
      <button
        className={`tvup-icon-button${unreadCount ? ' has-dot' : ''}`}
        type="button"
        aria-label={`Bildirimler${unreadCount ? `, ${unreadCount} okunmamış` : ''}`}
        aria-haspopup="dialog"
        aria-expanded={open}
        data-tooltip="Bildirimler"
        onClick={() => {
          const nextOpen = !open;
          setOpen(nextOpen);
          if (nextOpen) loadNotifications();
        }}
      >
        <NotificationsNoneRoundedIcon className="tvup-action-svg" />
        {unreadCount ? <span className="tvup-notifications-count">{unreadCount > 99 ? '99+' : unreadCount}</span> : null}
      </button>

      {open ? (
        <section className="tvup-notifications-popover" role="dialog" aria-label="Bildirimler">
          <header>
            <span><b>Bildirimler</b><small>{stats.total_count || notifications.length} kayıt</small></span>
            <span>
              <button type="button" aria-label="Bildirimleri yenile" disabled={loading} onClick={loadNotifications}><RefreshRoundedIcon /></button>
              <button type="button" aria-label="Bildirimleri kapat" onClick={() => setOpen(false)}><CloseRoundedIcon /></button>
            </span>
          </header>

          <div className="tvup-notifications-list" aria-live="polite">
            {loading ? (
              <div className="tvup-notifications-state"><CircularProgress size={25} /><b>Bildirimler yükleniyor</b></div>
            ) : error && notifications.length === 0 ? (
              <div className="tvup-notifications-state is-error"><NotificationsNoneRoundedIcon /><b>{error}</b></div>
            ) : notifications.length === 0 ? (
              <div className="tvup-notifications-state"><NotificationsNoneRoundedIcon /><b>Bildirim yok</b></div>
            ) : notifications.map((notification) => {
              const unread = Number(notification.okundu) === 0;
              return (
                <article key={notification.id} className={unread ? 'is-unread' : ''}>
                  <button type="button" className="tvup-notification-copy" disabled={actionId === `read-${notification.id}`} onClick={() => handleNotificationClick(notification)}>
                    <span><b>{notificationTitle(notification)}</b><time>{relativeTime(notification.zaman)}</time></span>
                    <p>{notificationPlainText(regionalNotificationContent(notification, currency))}</p>
                  </button>
                </article>
              );
            })}
          </div>

          {error && notifications.length ? <p className="tvup-notifications-error">{error}</p> : null}
          {notifications.length ? (
            <footer>
              <button type="button" disabled={Boolean(actionId)} onClick={clearAll}>
                {actionId === 'clear-all' ? <CircularProgress size={16} /> : <DoneAllRoundedIcon />}
                Tümünü Temizle
              </button>
            </footer>
          ) : null}
        </section>
      ) : null}

      <Dialog
        open={Boolean(selectedNotification)}
        onClose={() => setSelectedNotification(null)}
        maxWidth={false}
        transitionDuration={0}
        className="tvup-notification-detail-dialog"
        aria-labelledby="tvup-notification-detail-title"
        slotProps={{
          backdrop: { className: 'tvup-notification-detail-backdrop' },
          paper: { id: 'tvup-notification-detail-modal', className: 'tvup-notification-detail-modal' },
        }}
      >
        <header className="tvup-notification-detail-header">
          <span className="tvup-notification-detail-icon" aria-hidden="true"><NotificationsNoneRoundedIcon /></span>
          <span className="tvup-notification-detail-heading">
            <h2 id="tvup-notification-detail-title">Bildirim Detayı</h2>
            <time>{notificationDateLabel(selectedNotification?.zaman)}</time>
          </span>
          <button type="button" className="tvup-notification-detail-close" aria-label="Bildirim detayını kapat" onClick={() => setSelectedNotification(null)}>
            <CloseRoundedIcon />
          </button>
        </header>

        <div className="tvup-notification-detail-content">
          {selectedContent ? (
            <div
              className="tvup-notification-detail-html"
              role="document"
              dangerouslySetInnerHTML={{ __html: sanitizeNotificationHtml(selectedContent) }}
            />
          ) : (
            <div className="tvup-notification-detail-html" role="status">Bildirim içeriği bulunmuyor.</div>
          )}
        </div>

        <footer className="tvup-notification-detail-footer">
          <button
            type="button"
            className="is-danger"
            aria-label="Bildirimi sil"
            disabled={Boolean(actionId)}
            onClick={async () => {
              const notification = selectedNotification;
              const deleted = await deleteNotification(notification);
              if (deleted) setSelectedNotification(null);
            }}
          >
            {actionId === `delete-${selectedNotification?.id}` ? <CircularProgress size={16} /> : <DeleteOutlineRoundedIcon />}
            Bildirimi Sil
          </button>
          <button type="button" onClick={() => setSelectedNotification(null)}>Kapat</button>
          {canNavigateFromDetail ? (
            <button
              type="button"
              className="is-primary"
              onClick={() => {
                const notification = selectedNotification;
                setSelectedNotification(null);
                onNavigate?.(notification);
              }}
            >
              İlgili Sayfaya Git <ArrowForwardRoundedIcon />
            </button>
          ) : null}
        </footer>
      </Dialog>
    </div>
  );
}

export default UserPanelNotifications;
